import { supabase } from '@/integrations/supabase/client';
import { debugLog, debugError } from '@/utils/debugUtils';
import { SimpleNotification } from '@/hooks/useNotifications';

export class NotificationDataService {
  static async fetchNotifications(userId: string): Promise<{ notifications: SimpleNotification[]; unreadCount: number }> {
    try {
      // Verificar se há uma sessão autenticada
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id || session.user.id !== userId) {
        debugLog('notifications', 'Usuário não autenticado ou ID não corresponde');
        return { notifications: [], unreadCount: 0 };
      }

      // USAR RPC - zero queries extras, zero exposição de IDs, filtros no backend
      const { data, error } = await supabase.rpc('get_user_notifications', {
        p_limit: 50
      });

      if (error) throw error;

      const notifications: SimpleNotification[] = (data || []).map(n => ({
        id: n.id,
        title: n.title,
        message: n.message,
        notification_type: n.notification_type,
        category: n.category,
        is_read: n.is_read,
        created_at: n.created_at,
        source_id: n.source_id,
        user_avatar: n.user_avatar,
        username: n.username
      }));

      const unreadCount = notifications.filter(n => !n.is_read).length;

      debugLog('notifications', 'Fetched via RPC:', { 
        totalCount: notifications.length, 
        unreadCount 
      });

      return { notifications, unreadCount };
    } catch (error) {
      debugError('notifications', 'Error fetching notifications:', error);
      throw error;
    }
  }

  static async markAsRead(notificationId: string): Promise<boolean> {
    try {
      debugLog('notifications', 'Marking notification as read via RPC:', { notificationId });
      
      const { data, error } = await (supabase as any)
        .rpc('mark_notification_read', { notification_id: notificationId });

      if (error) {
        debugError('notifications', 'Error marking notification as read:', error);
        return false;
      }

      debugLog('notifications', 'Notification marked as read successfully');
      return data === true;
    } catch (error) {
      debugError('notifications', 'Error marking notification as read:', error);
      return false;
    }
  }

  static async removeNotification(notificationId: string): Promise<boolean> {
    try {
      debugLog('notifications', 'Attempting to delete notification via RPC:', notificationId);

      const { data, error } = await (supabase as any)
        .rpc('delete_notification', { notification_id: notificationId });

      if (error) {
        debugError('notifications', 'Error deleting notification:', error);
        return false;
      }

      debugLog('notifications', 'Notification deleted successfully:', notificationId);
      return data === true;
    } catch (error) {
      debugError('notifications', 'Error removing notification:', error);
      return false;
    }
  }

  /**
   * Marks all notifications as read (does NOT delete them)
   */
  static async markAllAsRead(): Promise<number> {
    try {
      debugLog('notifications', 'Marking all notifications as read via RPC');

      const { data, error } = await (supabase as any)
        .rpc('mark_all_notifications_read');

      if (error) {
        debugError('notifications', 'Error marking all as read:', error);
        throw error;
      }

      debugLog('notifications', 'All notifications marked as read:', data);
      return data || 0;
    } catch (error) {
      debugError('notifications', 'Error marking all as read:', error);
      throw error;
    }
  }

  /**
   * Deletes all notifications permanently
   */
  static async deleteAllNotifications(): Promise<number> {
    try {
      debugLog('notifications', 'Deleting all notifications via RPC');

      const { data, error } = await (supabase as any)
        .rpc('delete_all_notifications');

      if (error) {
        debugError('notifications', 'Error deleting all notifications:', error);
        throw error;
      }

      debugLog('notifications', 'All notifications deleted:', data);
      return data || 0;
    } catch (error) {
      debugError('notifications', 'Error deleting all notifications:', error);
      throw error;
    }
  }
}
